// Small presentational selectors shared by the overview screens.

import type {
  reconciliation_DebtorAccountDTO,
  reconciliation_DebtorAccountsSummaryResponse,
} from "@/api";
import type { AgingBucket, AgingSummary, DebtorOverviewRow } from "./types";

/** Plain-string debtor label without an i18n dependency (prototype). */
export function debtorRowLabel(account: reconciliation_DebtorAccountDTO): string {
  if (account.subsidyOfficeId) return account.displayName ?? "Subsidy office";
  if (account.familyGroupId && account.familySurname)
    return `Family ${account.familySurname}`;
  const contact = [account.contactFirstname, account.contactLastname]
    .filter(Boolean)
    .join(" ");
  return contact || account.displayName || "Debtor";
}

export function debtorChildrenSuffix(
  account: reconciliation_DebtorAccountDTO,
): string {
  const children = account.children ?? [];
  if (children.length === 0) return "";
  return children
    .map((c) => [c.childInternalId, c.firstname].filter(Boolean).join(" "))
    .join(", ");
}

const EMPTY_AGING: AgingSummary = {
  buckets: [
    { key: "current", label: "Current", cents: 0, invoiceCount: 0 },
    { key: "d1_30", label: "1–30 days", cents: 0, invoiceCount: 0 },
    { key: "d31_60", label: "31–60 days", cents: 0, invoiceCount: 0 },
    { key: "d61_90", label: "61–90 days", cents: 0, invoiceCount: 0 },
    { key: "d90plus", label: "90+ days", cents: 0, invoiceCount: 0 },
  ],
  totalCents: 0,
};

/**
 * Build the ORG-WIDE portfolio aging from the summary endpoint's aging buckets.
 * Prefer this over mergeAging(rows) for the overview Aging card: mergeAging only
 * sums the visible table page, whereas the summary aging covers the whole book
 * (and the five buckets sum to the org-wide outstanding). invoiceCount is 0 —
 * the summary reports bucket amounts, not per-bucket invoice counts.
 */
export function agingFromSummary(
  summary?: reconciliation_DebtorAccountsSummaryResponse,
): AgingSummary {
  const cents = (n?: number) => n ?? 0;
  const buckets: AgingBucket[] = [
    { key: "current", label: "Current", cents: cents(summary?.agingCurrentCents), invoiceCount: 0 },
    { key: "d1_30", label: "1–30 days", cents: cents(summary?.aging1To30Cents), invoiceCount: 0 },
    { key: "d31_60", label: "31–60 days", cents: cents(summary?.aging31To60Cents), invoiceCount: 0 },
    { key: "d61_90", label: "61–90 days", cents: cents(summary?.aging61To90Cents), invoiceCount: 0 },
    { key: "d90plus", label: "90+ days", cents: cents(summary?.aging90PlusCents), invoiceCount: 0 },
  ];
  return {
    buckets,
    totalCents: buckets.reduce((sum, b) => sum + b.cents, 0),
  };
}

/** Sum many debtors' aging into one portfolio aging summary. */
export function mergeAging(rows: DebtorOverviewRow[]): AgingSummary {
  const merged: AgingSummary = {
    buckets: EMPTY_AGING.buckets.map((b) => ({ ...b })),
    totalCents: 0,
  };
  for (const r of rows) {
    for (let i = 0; i < r.aging.buckets.length; i += 1) {
      merged.buckets[i].cents += r.aging.buckets[i].cents;
      merged.buckets[i].invoiceCount += r.aging.buckets[i].invoiceCount;
    }
    merged.totalCents += r.aging.totalCents;
  }
  return merged;
}

export interface PortfolioKpis {
  outstandingCents: number;
  creditCents: number;
  netCents: number;
  overdueCount: number;
  overdueCents: number;
  attentionCount: number;
  debtorCount: number;
}

export function portfolioKpis(rows: DebtorOverviewRow[]): PortfolioKpis {
  let outstanding = 0;
  let credit = 0;
  let net = 0;
  let overdueCount = 0;
  let overdueCents = 0;
  let attention = 0;
  for (const r of rows) {
    outstanding += r.account.totalOutstandingCents ?? 0;
    credit += r.account.unappliedCreditCents ?? 0;
    net += r.account.netBalanceCents ?? 0;
    overdueCount += r.reconciliation.overdueCount;
    overdueCents += r.reconciliation.overdueCents;
    if (r.reconciliation.state === "attention") attention += 1;
  }
  return {
    outstandingCents: outstanding,
    creditCents: credit,
    netCents: net,
    overdueCount,
    overdueCents,
    attentionCount: attention,
    debtorCount: rows.length,
  };
}
